import type { PatternProject } from '../types';

const DB_NAME = 'yxmpd-web-projects';
const STORE_NAME = 'projects';

export function ensureProjectIdentity(project: PatternProject): PatternProject {
  return project.projectUid ? project : { ...project, projectUid: `PRJ-${crypto.randomUUID()}` };
}

export function dedupeProjectsByUid(projects: PatternProject[]) {
  const byUid = new Map<string, PatternProject>();
  for (const project of projects) {
    const current = byUid.get(project.projectUid);
    if (!current || project.updatedAt > current.updatedAt) byUid.set(project.projectUid, project);
  }
  return [...byUid.values()].sort((left, right) => right.updatedAt - left.updatedAt);
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      store.createIndex('ownerKey', 'ownerKey');
      store.createIndex('updatedAt', 'updatedAt');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('无法打开本地项目库'));
  });
}

export async function saveProject(project: PatternProject) {
  project = ensureProjectIdentity(project);
  const db = await openDatabase();
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.index('ownerKey').getAll(project.ownerKey);
    request.onsuccess = () => {
      for (const existing of request.result as PatternProject[]) {
        if (existing.id !== project.id && existing.projectUid === project.projectUid) store.delete(existing.id);
      }
      store.put(project);
    };
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); reject(transaction.error); };
  });
}

export async function listProjects(ownerKey: string): Promise<PatternProject[]> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.index('ownerKey').getAll(ownerKey);
    let projects: PatternProject[] = [];
    request.onsuccess = () => {
      const stored = request.result as PatternProject[];
      const identified = stored.map(ensureProjectIdentity);
      identified.forEach((project, index) => {
        if (project !== stored[index]) store.put(project);
      });
      projects = dedupeProjectsByUid(identified);
      const retainedIds = new Set(projects.map((project) => project.id));
      for (const project of identified) if (!retainedIds.has(project.id)) store.delete(project.id);
    };
    transaction.oncomplete = () => { db.close(); resolve(projects); };
    transaction.onerror = () => { db.close(); reject(transaction.error || request.error); };
  });
}

export async function deleteProject(id: string) {
  return deleteProjects([id]);
}

export async function deleteProjects(ids: string[]) {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (!uniqueIds.length) return;
  const db = await openDatabase();
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    for (const id of uniqueIds) store.delete(id);
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); reject(transaction.error); };
  });
}
